import React, { useState, useEffect } from 'react';
import {
  fetchHeadings,
  calculateMaterials,
} from '../../services/materialCalculatorAPI';
import MaterialResult from './MaterialResult';
import * as XLSX from 'xlsx';

const MaterialCalculator = () => {
  const [headings, setHeadings] = useState([]);
  const [selectedHeading, setSelectedHeading] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [quantity, setQuantity] = useState('');
  const [result, setResult] = useState(null);
  const [finalTable, setFinalTable] = useState([]);

  useEffect(() => {
    fetchHeadings().then(data => setHeadings(data));
  }, []);

  const handleCalculate = async () => {
    if (!selectedHeading || !quantity) return;
    const res = await calculateMaterials(selectedHeading.id, quantity);
    setResult(res.materials || []);
  };

  const handleAddToFinalTable = () => {
    if (!selectedHeading || !result?.length) return;

    setFinalTable(prev => [
      ...prev,
      {
        headingName: selectedHeading.name,
        headingUnit: selectedHeading.unit,
        quantity: quantity,
        materials: result,
      },
    ]);

    setSelectedHeading(null);
    setSearchTerm('');
    setQuantity('');
    setResult(null);
  };

  const handleExport = () => {
    const rows = [];

    finalTable.forEach(section => {
      rows.push({
        'Work Heading': section.headingName,
        'Input Quantity': section.quantity,
        'Unit': section.headingUnit,
      });

      section.materials.forEach(material => {
        rows.push({
          Material: material.material_name,
          Quantity: material.required_quantity,
          Unit: material.material_unit,
        });
      });

      rows.push({}); // Spacer row
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Materials');

    XLSX.writeFile(workbook, 'Material_Summary.xlsx');
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="text-xl font-bold mb-4">📐 Material Calculator</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="relative">
          <label className="font-medium">Choose Work Heading:</label>
          <input
            type="text"
            className="w-full border p-2 rounded"
            placeholder="Type to search..."
            value={searchTerm}
            onChange={e => {
              setSearchTerm(e.target.value);
              setSelectedHeading(null);
            }}
          />

          {searchTerm.length > 0 && !selectedHeading && (
            <ul className="absolute z-10 bg-white border w-full rounded shadow max-h-40 overflow-y-auto">
              {headings
                .filter(h =>
                  h.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(h => (
                  <li
                    key={h.id}
                    className="p-2 hover:bg-blue-100 cursor-pointer"
                    onClick={() => {
                      setSelectedHeading(h);
                      setSearchTerm(h.name);
                    }}
                  >
                    {h.name} ({h.unit})
                  </li>
                ))}
            </ul>
          )}
        </div>

        <div>
          <label className="font-medium">Enter Quantity:</label>
          <input
            type="number"
            className="w-full border p-2 rounded"
            value={quantity}
            onChange={e => setQuantity(e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={handleCalculate}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Calculate Materials
        </button>

        {result && (
          <button
            onClick={handleAddToFinalTable}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            ➕ Add to Final Table
          </button>
        )}
      </div>

      {result && <MaterialResult materials={result} />}

      {finalTable.length > 0 && (
        <div className="mt-10">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-bold">🧾 Final Material Summary</h3>
            <button
              onClick={handleExport}
              className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
            >
              📤 Export to Excel
            </button>
          </div>

          {finalTable.map((section, idx) => (
            <div key={idx} className="border p-4 rounded mb-4 bg-gray-50">
              <h4 className="text-md font-semibold mb-2">
                {section.headingName} ({section.quantity} {section.headingUnit})
              </h4>
              <table className="w-full text-sm border">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="border px-2 py-1 text-left">Material</th>
                    <th className="border px-2 py-1 text-left">Quantity</th>
                    <th className="border px-2 py-1 text-left">Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {section.materials.map((mat, i) => (
                    <tr key={i}>
                      <td className="border px-2 py-1">{mat.material_name}</td>
                      <td className="border px-2 py-1">{mat.required_quantity}</td>
                      <td className="border px-2 py-1">{mat.material_unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MaterialCalculator;
